/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import findIndex from 'lodash/findIndex';
import get from 'lodash/get';

import { TypedUseSelectorHook, useSelector } from 'react-redux';
import type { Reducer } from 'redux';
import type { UserSummaryResponse } from '../actions/availabilityAlerts';
import {
    CREATE_AVAILALERT,
    CREATE_AVAILALERT_ERROR,
    DEACTIVATE_AVAILALERT,
    DEACTIVATE_AVAILALERT_ERROR,
    DEACTIVATE_AVAILALERT_LOADING,
    DELETE_AVAILALERT,
    DELETE_AVAILALERT_ERROR,
    DELETE_AVAILALERT_LOADING,
    GET_AVAILALERTS,
    GET_AVAILALERTS_ERROR,
    GET_AVAILALERTS_LOADING,
    GET_AVAILALERT_COUNTS,
    GET_AVAILALERT_COUNTS_ERROR,
    GET_AVILALERTS_SUMMARY_ACTIVE,
    GET_AVILALERTS_SUMMARY_ERROR,
    GET_AVILALERTS_SUMMARY_INACTIVE,
} from '../constants/index';

export type AvailAlertsState = {
    availAlerts: {
        active: UserSummaryResponse[];
        inactive: UserSummaryResponse[];
    };
    availAlertsCount: any;
    deactivating: boolean;
    deleting: boolean;
    loading: boolean;
    list: any[];
    updating: boolean;
    err?: string;
};

export const useAvailAlertsSelector: TypedUseSelectorHook<{
    availabilityAlerts: AvailAlertsState;
}> = useSelector;

const INITIALSTATE: AvailAlertsState = {
    availAlerts: { active: [], inactive: [] },
    availAlertsCount: {},
    deactivating: false,
    deleting: false,
    loading: false,
    list: [],
    updating: false,
};

const availabilityAlertReducers: Reducer<
    typeof INITIALSTATE,
    { type: string; payload: UserSummaryResponse | any; error?: any }
> = (state = INITIALSTATE, action) => {
    const st = { ...state };

    switch (action.type) {
        case GET_AVILALERTS_SUMMARY_ACTIVE:
        case GET_AVILALERTS_SUMMARY_INACTIVE:
            if (!action.payload) {
                st.loading = false;
                if (action.type === GET_AVILALERTS_SUMMARY_ACTIVE) {
                    st.availAlerts.active = [];
                } else {
                    st.availAlerts.inactive = [];
                }
                return st;
            }

            if (action.payload[0].is_active) {
                st.availAlerts.active = action.payload;
            } else {
                st.availAlerts.inactive = action.payload;
            }

            st.loading = false;
            return st;
        case GET_AVILALERTS_SUMMARY_ERROR:
        case GET_AVAILALERTS_ERROR:
            st.loading = false;
            st.err =
                action.error ||
                'There is a problem in retrieving the availability alerts';
            return st;

        case GET_AVAILALERTS:
            st.list = action.payload;
            st.loading = false;
            return st;

        case GET_AVAILALERTS_LOADING:
            st.loading = true;
            return st;

        case GET_AVAILALERT_COUNTS:
            delete st.err;
            // pull always the camping counts
            st.availAlertsCount = get(action.payload, 'inventory.camping', {});
            return st;

        case GET_AVAILALERT_COUNTS_ERROR:
            delete st.err;
            st.availAlertsCount.err =
                action.error ||
                'There is a problem in retrieving availability alert information';
            return st;

        case DEACTIVATE_AVAILALERT: {
            const alertItemIndex = findIndex(
                st.list,
                (a) => a.notification_id === action.payload
            );
            let newAlerts = [...st.list];

            if (alertItemIndex >= 0) {
                const alertItem = { ...st.list[action.payload], is_active: false };
                newAlerts = [
                    ...st.list.slice(0, alertItemIndex),
                    alertItem,
                    ...st.list.slice(alertItemIndex + 1),
                ];
            }

            return {
                ...st,
                list: newAlerts,
                deactivating: false,
            };
        }

        case DEACTIVATE_AVAILALERT_ERROR:
            return {
                ...st,
                deactivating: false,
                err: action.error || 'There was an issue deleting the availability alert',
            };

        case DEACTIVATE_AVAILALERT_LOADING:
            return { ...st, deactivating: true };

        case DELETE_AVAILALERT: {
            st.deleting = false;
            const alertIndex = findIndex(
                st.list,
                (a) => a.notification_id === action.payload
            );
            if (alertIndex > -1) {
                st.list.splice(alertIndex, 1);
            }
            return st;
        }

        case DELETE_AVAILALERT_ERROR:
            st.deleting = false;
            st.err = action.error || 'There was an issue deleting the availability alert';
            return st;

        case DELETE_AVAILALERT_LOADING:
            st.deleting = true;
            return st;

        case CREATE_AVAILALERT: {
            const alertItem = { ...action.payload };
            const existingAlertIndex = findIndex(
                st.list,
                (a) => a.notification_id === action.payload.notification_id
            );
            let newAlerts = [...st.list];

            if (existingAlertIndex >= 0) {
                newAlerts = [
                    ...st.list.slice(0, existingAlertIndex),
                    alertItem,
                    ...st.list.slice(existingAlertIndex + 1),
                ];
            }
            const newState = { ...st, list: newAlerts, availAlerts: alertItem };
            delete newState.err;

            return newState;
        }

        case CREATE_AVAILALERT_ERROR: {
            const newState = {
                ...st,
                availAlerts: {
                    ...st.availAlerts,
                    err:
                        action.error ||
                        'There is a problem in trying to create your alert.',
                },
            };
            delete newState.err;

            return newState;
        }

        default:
            return st;
    }
};

export default availabilityAlertReducers;
